/**
 * /tools — public free tools hub.
 * Currently ships the Subnet Calculator + 5-6 "Coming soon" placeholders.
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    Calculator, ShieldCheck, AlertTriangle, Lock, FileCheck,
    KeyRound, ClipboardCheck, ArrowRight, Bell, Radar,
} from "lucide-react";
import { postJSON } from "../lib/api";
import { toast } from "sonner";
import { usePageMeta } from "../lib/usePageMeta";
import { SEO } from "../components/SEO";

const TOOLS = [
    {
        id: "subnet-calculator", title: "Subnet Calculator",
        description: "Compute network, broadcast, usable range, hosts, CIDR & wildcard mask.",
        icon: Calculator, href: "/tools/subnet-calculator", status: "live",
    },
    {
        id: "dns-health-check", title: "DNS Health Check",
        description: "Audit SPF, DKIM, DMARC, DNSSEC, MTA-STS, and 4 major blacklists in seconds.",
        icon: ShieldCheck, href: "/tools/dns-health", status: "live",
    },
    {
        id: "cve-lookup", title: "CVE Lookup for your firewall",
        description: "Check your Fortinet, WatchGuard, Zyxel, or pfSense firmware against the official NVD database.",
        icon: AlertTriangle, href: "/tools/cve-lookup", status: "live",
    },
    {
        id: "tls-inspector", title: "TLS / SSL Inspector",
        description: "Cert chain, protocol matrix (TLS 1.0-1.3), HSTS, key size — graded A-F.",
        icon: Lock, href: "/tools/tls-inspector", status: "live",
    },
    {
        id: "headers-analyzer", title: "Security Headers Analyzer",
        description: "Grade CSP, HSTS, X-Frame-Options, Permissions-Policy and more out of 100.",
        icon: FileCheck, href: "/tools/security-headers", status: "live",
    },
    {
        id: "password-breach", title: "Password Breach Check",
        description: "Privacy-first k-anonymous lookup against 850M+ leaked password hashes.",
        icon: KeyRound, href: "/tools/password-breach", status: "live",
    },
    {
        id: "public-audit", title: "Public Surface Audit",
        description: "Discover subdomains & exposed services via Certificate Transparency (crt.sh) + DNS — risk-scored.",
        icon: Radar, href: "/tools/public-audit", status: "live",
    },
    {
        id: "nis2-self", title: "NIS2 Readiness Assessment",
        description: "15-question audit across NIS2 Article 21 measures, with PDF roadmap.",
        icon: ClipboardCheck, href: "/tools/nis2-assessment", status: "live",
    },
];

const ComingSoonForm = ({ toolId, onDone }) => {
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!email) return;
        setBusy(true);
        try {
            await postJSON("/waitlist", { email, source: `tool:${toolId}` });
            // Fire-and-forget interest signal (separate from /waitlist)
            postJSON("/tools/usage", {
                tool_id: toolId,
                action: "notify_clicked",
            }).catch(() => {});
            toast.success("Got it — we'll email you when this tool launches.");
            setEmail("");
            onDone?.();
        } catch (err) {
            toast.error(err?.message || "Couldn't subscribe right now. Please try again.");
        } finally {
            setBusy(false);
        }
    };
    return (
        <form onSubmit={submit} className="mt-3 flex gap-2" data-testid={`notify-form-${toolId}`}>
            <input
                type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="flex-1 min-w-0 px-3 py-1.5 bg-canvas border border-white/[0.08] rounded-md text-[12.5px] focus:outline-none focus:border-accent-cyan"
            />
            <button type="submit" disabled={busy}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-accent-cyan text-canvas text-[12.5px] font-medium disabled:opacity-50">
                <Bell className="w-3.5 h-3.5" /> {busy ? "…" : "Notify me"}
            </button>
        </form>
    );
};

const ToolCard = ({ tool }) => {
    const [showNotify, setShowNotify] = useState(false);
    const Icon = tool.icon;

    if (tool.status === "live") {
        return (
            <Link to={tool.href} data-testid={`tool-card-${tool.id}`}
                className="group block p-5 rounded-xl border border-white/[0.06] bg-surface/70 backdrop-blur-md hover:border-accent-cyan/40 hover:shadow-glow-cyan transition-all">
                <div className="flex items-start justify-between">
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-accent-cyan/10 border border-accent-cyan/30 text-accent-cyan">
                        <Icon className="w-5 h-5" />
                    </span>
                    <span className="text-[10px] font-mono uppercase tracking-[0.22em] text-accent-mint">Live</span>
                </div>
                <h3 className="mt-4 font-display font-semibold text-[16px] text-text-primary">{tool.title}</h3>
                <p className="mt-1.5 text-[12.5px] text-text-muted leading-relaxed">{tool.description}</p>
                <p className="mt-4 inline-flex items-center gap-1.5 text-[12px] font-medium text-accent-cyan group-hover:translate-x-0.5 transition-transform">
                    Try it <ArrowRight className="w-3.5 h-3.5" />
                </p>
            </Link>
        );
    }

    return (
        <div data-testid={`tool-card-${tool.id}`}
            className="p-5 rounded-xl border border-white/[0.06] bg-surface/40 backdrop-blur-md">
            <div className="flex items-start justify-between">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-white/[0.03] border border-white/[0.06] text-text-muted">
                    <Icon className="w-5 h-5" />
                </span>
                <span className="text-[10px] font-mono uppercase tracking-[0.22em] text-accent-amber">Coming soon</span>
            </div>
            <h3 className="mt-4 font-display font-semibold text-[16px] text-text-primary">{tool.title}</h3>
            <p className="mt-1.5 text-[12.5px] text-text-muted leading-relaxed">{tool.description}</p>
            {showNotify ? (
                <ComingSoonForm toolId={tool.id} onDone={() => setShowNotify(false)} />
            ) : (
                <button type="button" onClick={() => setShowNotify(true)} data-testid={`tool-notify-${tool.id}`}
                    className="mt-4 inline-flex items-center gap-1.5 text-[12px] text-text-muted hover:text-accent-cyan transition-colors">
                    <Bell className="w-3.5 h-3.5" /> Notify me when this launches
                </button>
            )}
        </div>
    );
};

export const Tools = () => {
    usePageMeta({
        title: "Free Tools — Subnet, DNS, CVE, TLS, Headers, Password Breach, NIS2",
        description:
            "Seven free networking & security tools by Cybersecurity Forefront: Subnet Calculator, DNS Health Check, Firewall CVE Lookup, TLS Inspector, Security Headers Analyzer, Password Breach Check, and NIS2 Readiness Assessment.",
    });
    React.useEffect(() => {
        // page_view telemetry (once per mount)
        postJSON("/tools/usage", { tool_id: "tools-hub", action: "page_view" }).catch(() => {});
    }, []);
    return (
        <div className="pt-24 sm:pt-32 pb-20 mx-auto max-w-6xl px-5" data-testid="tools-page">
            <SEO
                title="Free Security Tools — Subnet, DNS, TLS, CVE & NIS2"
                description="Free, no-login security tools: subnet calculator (IPv4/IPv6), DNS health, TLS inspector, security headers, password breach lookup, CVE search, and NIS2 self-assessment."
                canonicalPath="/tools"
            />
            <motion.header
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
                className="text-center mb-12"
            >
                <p className="text-[10.5px] font-mono uppercase tracking-[0.32em] text-accent-cyan">/tools</p>
                <h1 className="mt-3 font-display font-semibold text-4xl sm:text-5xl tracking-tight">
                    Free networking & <span className="text-accent-cyan">security tools</span>
                </h1>
                <p className="mt-4 text-[14px] text-text-muted max-w-xl mx-auto leading-relaxed">
                    Built by Cybersecurity Forefront. Use them anytime — no signup needed.
                </p>
            </motion.header>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="tools-grid">
                {TOOLS.map((t) => <ToolCard key={t.id} tool={t} />)}
            </div>

            <motion.div
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
                className="mt-14 rounded-2xl border border-accent-cyan/15 bg-accent-cyan/[0.04] backdrop-blur-md p-7 text-center"
            >
                <h2 className="font-display font-semibold text-xl">Need the full toolkit?</h2>
                <p className="mt-2 text-[13.5px] text-text-muted max-w-xl mx-auto">
                    Sign up to access ZeroConf — our multi-vendor firewall provisioning wizard with 37 supported models, live CLI preview, and saved configurations.
                </p>
                <Link to="/pricing" className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-md bg-accent-cyan text-canvas text-[13px] font-medium hover:shadow-glow-cyan transition-shadow">
                    Explore Pro <ArrowRight className="w-3.5 h-3.5" />
                </Link>
            </motion.div>
        </div>
    );
};

export default Tools;
